import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import DocumentUploadStep from "@/components/onboarding/DocumentUploadStep";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";

type OrgType = 'sfo' | 'mfo';

export default function Onboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [orgType, setOrgType] = useState<OrgType>('sfo');
  const [orgName, setOrgName] = useState('');
  const [baseCcy, setBaseCcy] = useState('SGD');
  const [tz, setTz] = useState('Asia/Singapore');
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState<string>('');
  const [createdFamilyId, setCreatedFamilyId] = useState<string>('');

  const handleCreateOrg = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create org
      const { data: org, error: orgError } = await supabase
        .from('orgs')
        .insert({
          name: orgName,
          org_type: orgType,
          base_ccy: baseCcy,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create default family
      const { data: family, error: familyError } = await supabase
        .from('families')
        .insert({
          org_id: org.id,
          name: familyName || orgName,
          onboarding_status: 'data_pending',
        })
        .select()
        .single();

      if (familyError) throw familyError;

      // Assign OrgAdmin role to creator
      const { error: roleError } = await supabase
        .from('users_org_roles')
        .insert({
          user_id: user.id,
          org_id: org.id,
          role: 'OrgAdmin',
        });

      if (roleError) throw roleError;

      // For SFO, also assign FamilyAdmin to default family
      if (orgType === 'sfo') {
        await supabase
          .from('users_family_roles')
          .insert({
            user_id: user.id,
            family_id: family.id,
            role: 'FamilyAdmin',
          });
      }

      // Create a default client so document uploads work immediately
      const { error: clientError } = await supabase
        .from('clients')
        .insert({
          name: familyName || orgName,
          org_id: org.id,
          family_id: family.id,
        });

      if (clientError) console.warn('Failed to create default client:', clientError);

      toast.success('Organization created! Now import your portfolio data.', { duration: 6000 });

      // Invalidate and wait for queries to refetch
      await queryClient.invalidateQueries({ queryKey: ['org-roles'], refetchType: 'active' });

      // Wait for the data to be available
      let attempts = 0;
      while (attempts < 10) {
        const orgRolesCache = queryClient.getQueryData(['org-roles', user.id]);
        if (orgRolesCache && Array.isArray(orgRolesCache) && orgRolesCache.length > 0) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }

      setCreatedOrgId(org.id);
      setCreatedFamilyId(family.id);
      setStep(2);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Welcome to Vintage</CardTitle>
          <CardDescription>
            Let's set up your family office workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <OnboardingProgress status={step === 1 ? "draft" : "data_pending"} />

          {step === 1 && (
            <>
              <div className="space-y-4">
                <Label>Choose your office type</Label>
                <RadioGroup value={orgType} onValueChange={(v) => setOrgType(v as OrgType)}>
                  <div className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-accent">
                    <RadioGroupItem value="sfo" id="sfo" />
                    <div className="flex-1">
                      <Label htmlFor="sfo" className="cursor-pointer font-medium">
                        Single Family Office (SFO)
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Manage your own family's wealth
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-accent">
                    <RadioGroupItem value="mfo" id="mfo" />
                    <div className="flex-1">
                      <Label htmlFor="mfo" className="cursor-pointer font-medium">
                        Multi Family Office (MFO)
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Manage multiple family clients
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="e.g., Harbour Family Office"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="baseCcy">Base Currency</Label>
                    <Select value={baseCcy} onValueChange={setBaseCcy}>
                      <SelectTrigger id="baseCcy">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="HKD">HKD - Hong Kong Dollar</SelectItem>
                        <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                        <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                        <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                        <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                        <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="tz">Time Zone</Label>
                    <Select value={tz} onValueChange={setTz}>
                      <SelectTrigger id="tz">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/New_York">Eastern Time (EST/EDT)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time (PST/PDT)</SelectItem>
                        <SelectItem value="UTC">GMT/UTC</SelectItem>
                        <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                        <SelectItem value="Europe/Zurich">Central European Time</SelectItem>
                        <SelectItem value="Asia/Singapore">Singapore Time</SelectItem>
                        <SelectItem value="Asia/Hong_Kong">Hong Kong Time</SelectItem>
                        <SelectItem value="Asia/Dubai">Gulf Time (UAE)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {orgType === 'mfo' && (
                  <div>
                    <Label htmlFor="familyName">First Family Name</Label>
                    <Input
                      id="familyName"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      placeholder="e.g., Harbour Family"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={handleCreateOrg}
                  disabled={!orgName || loading}
                >
                  {loading ? 'Creating...' : 'Create Organization'}
                </Button>
              </div>
            </>
          )}

          {step === 2 && createdOrgId && createdFamilyId && (
            <DocumentUploadStep
              orgId={createdOrgId}
              familyId={createdFamilyId}
              onComplete={() => {
                navigate('/');
              }}
              onSkip={() => {
                navigate('/');
              }}
            />
          )}

        </CardContent>
      </Card>
    </div>
  );
}
